import { ActionPlanStatistic } from 'src/services/plan';

import { DepartmentItem } from 'src/types/category';

import { User } from './user';
import { Project } from './project';
import { VarianceAnalysis } from './varianceAnalysis';
import { httpClient, QueryParams } from './httpClient';

export enum ActionPlanStatus {
  NotStarted = 'NotStarted',

  Completed = 'Completed',
}

type StatusMap = {
  [key in ActionPlanStatus]: string;
};

export const STATUS_TEXT_MAP: StatusMap = {
  [ActionPlanStatus.NotStarted]: '未完成',

  [ActionPlanStatus.Completed]: '已完成',
};

export interface ActionPlanList {
  total: number;
  page: number;
  size: number;
  records: ActionPlan[];
  statistic: ActionPlanStatistic;
}

export interface ActionPlanCreate {
  description?: string;
  planStartDate: string;
  planEndDate: string;
  department?: DepartmentItem;
  coordinators?: User[];
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  status?: ActionPlanStatus;
}

export interface ActionPlan {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  code?: string;
  source?: string;
  description?: string;
  planStartDate?: string;
  planEndDate?: string;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  status?: ActionPlanStatus;
  department?: DepartmentItem;
  dimension?: DepartmentItem;
  coordinators?: User[];
  creator?: User;
  lastUpdater?: User;
  records?: {
    attachments: string[];
    description: string;
  };
  project?: Project | null;
  varianceAnalysis?: VarianceAnalysis | null;
  assessmentDimension?: 'Duration' | 'Quantity' | 'Boolean';
  metadata?: {
    progressStatus?: 'Completed' | 'NotCompleted';
    planQuantity?: number;
    actualQuantity?: number;
    unit?: string;
  };
}

export interface GetAllActionPlansParams {
  page?: number;
  size?: number;
}

export interface GetMyActionPlansParams {
  startDate: string;
  endDate: string;
}

export interface FilterActionPlan {
  code?: string;
  description?: string;
  source?: string;
  creatorId?: number;
  lastUpdaterId?: number;
  createdAt?: {
    min?: string;
    max?: string;
  };
  departmentId?: string;
  departmentIds?: string[]; // 用于表格组件回填多选值
  dimensionId?: string;
  coordinatorIds?: number[];
  planStartDate?: {
    min: string;
    max: string;
  };
  planEndDate?: {
    min: string;
    max: string;
  };
  actualStartDate?: {
    min: string;
    max: string;
  };
  actualEndDate?: {
    min: string;
    max: string;
  };
  status?: ActionPlanStatus;
}

export async function getAllActionPlans(params?: GetAllActionPlansParams): Promise<ActionPlanList> {
  const queryParams: QueryParams = {};

  if (params?.page !== undefined) {
    queryParams.page = params.page.toString();
  }

  if (params?.size !== undefined) {
    queryParams.size = params.size.toString();
  }

  const response = await httpClient.sendRequest({
    url: `action_plans`,
    method: 'GET',
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });

  return response.data as ActionPlanList;
}

/**
 * 获取指定日期范围内的我的行动计划
 * @param params 查询参数，包括起止日期
 * @returns 行动计划列表
 */
export async function getMyActionPlans(params: GetMyActionPlansParams): Promise<ActionPlan[]> {
  const queryParams: QueryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
  };

  const response = await httpClient.sendRequest({
    url: `action_plans/my_action_plans`,
    method: 'GET',
    queryParams,
  });

  return response.data;
}

export async function getActionPlan(id: number | string): Promise<ActionPlan> {
  const response = await httpClient.sendRequest({
    url: `action_plans/${id}`,
    method: 'GET',
  });
  return response.data;
}

export async function createActionPlan(
  payload: Partial<Omit<ActionPlan, 'id'>>
): Promise<ActionPlan> {
  const response = await httpClient.sendRequest({
    url: `action_plans`,
    method: 'POST',
    data: payload,
  });

  return response.data;
}

export async function updateActionPlan(payload: Partial<ActionPlan>): Promise<ActionPlan> {
  const response = await httpClient.sendRequest({
    url: `action_plans`,
    method: 'PUT',
    data: payload,
  });

  return response.data;
}

export async function deleteActionPlan(id: string): Promise<ActionPlan> {
  const response = await httpClient.sendRequest({
    url: `action_plans`,
    method: 'DELETE',
    data: {
      id,
    },
  });
  return response.data;
}

export async function filterActionPlans(
  payload: FilterActionPlan,
  params?: GetAllActionPlansParams
): Promise<ActionPlanList> {
  try {
    const queryParams: Record<string, string> = {};
    if (params?.page !== undefined) {
      queryParams.page = params.page.toString();
    }
    if (params?.size !== undefined) {
      queryParams.size = params.size.toString();
    }
    const response = await httpClient.sendRequest({
      url: 'action_plans/search',
      method: 'POST',
      data: payload,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
    // 检查响应格式
    if (response.data && response.data.records && Array.isArray(response.data.records)) {
      // 后端返回的是分页格式，直接返回
      return response.data as ActionPlanList;
    }
    if (Array.isArray(response.data)) {
      // 后端直接返回了数组，需要转换为分页格式
      return {
        records: response.data,
        total: response.data.length,
        page: params?.page || 1,
        size: params?.size || response.data.length,
        statistic: {
          total: 0,
          notStarted: 0,
          completed: 0,
          completionRate: 0,
        },
      };
    }
    return {
      records: [],
      total: 0,
      page: params?.page || 1,
      size: params?.size || 0,
      statistic: {
        total: 0,
        notStarted: 0,
        completed: 0,
        completionRate: 0,
      },
    };
  } catch (error) {
    // 错误处理
    return {
      records: [],
      total: 0,
      page: params?.page || 1,
      size: params?.size || 0,
      statistic: {
        total: 0,
        notStarted: 0,
        completed: 0,
        completionRate: 0,
      },
    };
  }
}
